import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { safeAssert } from 'src/shared/lib/utils';
import {
  ActualsMeta,
  ActualsMetaNer,
  ActualsMetaNonNer,
  EmailMessage,
  Entities,
  MessageType,
} from 'src/features/messages/types/message.types';

export type ModelMutationProps = {
  integrationId: string;
  threadId: string;
  modelId: string;
  tagInternalId?: string;
  entities?: Entities[string];
  facetValues?: string[];
  meta?: ActualsMeta;
};

type LazyMessageSkeleton = {
  thread_id: string;
  integration_id: string;
  facets?: Record<string, string[]>;
  entities?: Record<string, Entities[keyof Entities]>;
  facets_versions?: Record<string, string>;
  actuals?: Record<string, ActualsMeta>;
};

export function useMutateMessage() {
  const { orgFetch } = useOrg();
  const { errorToast } = useToast();
  const queryClient = useQueryClient();
  const { role } = useOrg();

  const messagesQueryKey = ['messages-v3'];
  const lazyMessagesQueryKey = ['messages-v3-lazy'];

  const updateMessage = useMutation({
    mutationFn: async ({
      threadId,
      integrationId,
      modelId,
      tagInternalId,
      entities,
      facetValues,
      meta,
    }: ModelMutationProps) => {
      safeAssert(
        tagInternalId !== undefined ||
          entities !== undefined ||
          (Array.isArray(facetValues) && facetValues.length >= 0),
        'Either tagInternalId, entities or facetValues must be provided'
      );

      if (['SALES'].includes(role)) {
        return;
      }

      const facetValue = entities
        ? entities
        : Array.isArray(facetValues)
          ? facetValues
          : tagInternalId
            ? [tagInternalId]
            : null;

      const res = await orgFetch(`/api/v2/messages/feedback?integration=${integrationId}`, {
        method: 'post',
        body: JSON.stringify({
          threadId,
          integrationId,
          facet: modelId,
          facetValue,
          meta,
        }),
      });

      await res.json();

      if (!res.ok) {
        throw new Error('Model update failed');
      }

      return facetValue;
    },
    onSuccess: (_, variables, __) => {
      // Extract common logic for calculating nextValues
      const getNextValues = (): string[] => {
        if (Array.isArray(variables.facetValues)) {
          return variables.facetValues;
        }
        if (variables.tagInternalId) {
          return [variables.tagInternalId];
        }
        return [];
      };

      // Extract common logic for updating facets_versions
      const updateFacetsVersions = (
        currentVersions: Record<string, string> | undefined,
        nextValues: string[]
      ): Record<string, string> => {
        const facets_versions = { ...(currentVersions || {}) };
        if (variables.meta?.verified && nextValues.length > 0) {
          facets_versions[variables.modelId] = 'actual';
        } else if (!variables.meta?.verified || nextValues.length === 0) {
          delete facets_versions[variables.modelId];
        }
        return facets_versions;
      };

      const applyMutationToMessage = (message: MessageType): MessageType => {
        if (variables.entities) {
          return {
            ...message,
            entities: {
              ...message.entities,
              [variables.modelId]: variables.entities,
            },
            actuals: {
              ...message.actuals,
              [variables.modelId]: variables.meta as ActualsMetaNer,
            },
          };
        }

        const nextValues = getNextValues();
        const facets_versions = updateFacetsVersions(message.facets_versions, nextValues);

        return {
          ...message,
          facets_versions,
          models: {
            ...message.models,
            [variables.modelId]: nextValues,
          },
          actuals: {
            ...message.actuals,
            [variables.modelId]: variables.meta as ActualsMetaNonNer,
          },
        };
      };

      const applyMutationToSkeleton = (thread: LazyMessageSkeleton): LazyMessageSkeleton => {
        if (variables.entities) {
          return {
            ...thread,
            entities: {
              ...(thread.entities || {}),
              [variables.modelId]: variables.entities,
            },
            actuals: {
              ...(thread.actuals || {}),
              [variables.modelId]: variables.meta as ActualsMetaNer,
            },
          };
        }

        const nextValues = getNextValues();
        const facets = { ...(thread.facets || {}) };
        if (nextValues.length > 0) {
          facets[variables.modelId] = nextValues;
        } else {
          delete facets[variables.modelId];
        }

        const facets_versions = updateFacetsVersions(thread.facets_versions, nextValues);

        return {
          ...thread,
          facets,
          facets_versions,
          actuals: {
            ...(thread.actuals || {}),
            [variables.modelId]: variables.meta as ActualsMetaNonNer,
          },
        };
      };

      const updateQueries = <T extends { thread_id: string; integration_id: string }>(
        key: string[],
        updater: (message: T) => T
      ) => {
        queryClient.setQueriesData(
          { queryKey: key },
          (oldData: InfiniteData<{ data: T[]; nextToken?: string }> | undefined) => {
            if (!oldData) {
              return oldData;
            }
            const { pages, ...rest } = oldData;
            return {
              ...rest,
              pages: pages.map((page) => ({
                ...page,
                data: page.data.map((message) => {
                  const matches =
                    message.thread_id === variables.threadId && message.integration_id === variables.integrationId;
                  return matches ? updater(message) : message;
                }),
              })),
            };
          }
        );
      };

      queryClient.cancelQueries({ queryKey: messagesQueryKey });
      queryClient.cancelQueries({ queryKey: lazyMessagesQueryKey });
      updateQueries<MessageType>(messagesQueryKey, applyMutationToMessage);
      updateQueries<LazyMessageSkeleton>(lazyMessagesQueryKey, applyMutationToSkeleton);
    },
    onError: (error) => {
      console.log(error);
      errorToast('Failed to update message');
    },
  });

  const getFullThread = useMutation({
    mutationFn: async ({ threadId, integrationId }: { threadId: string; integrationId: string }) => {
      const res = await orgFetch<{ thread: EmailMessage[] }>(
        `/api/v2/messages/expand?threadId=${threadId}&integration=${integrationId}`
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error('Failed to fetch full thread');
      }

      return data.thread;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: messagesQueryKey },
        (oldData: InfiniteData<{ data: MessageType[]; nextToken: string }> | undefined) => {
          if (!oldData) {
            return oldData;
          }
          const { pages, ...rest } = oldData;
          return {
            ...rest,
            pages: pages.map((page) => ({
              ...page,
              data: page.data.map((message) => {
                const { thread_id, integration_id } = message;
                if (thread_id === variables.threadId && integration_id === variables.integrationId) {
                  return {
                    ...message,
                    fullThread: data,
                  };
                }
                return message;
              }),
            })),
          };
        }
      );
    },
    onError: (error) => {
      console.log(error);
      if (process.env.NODE_ENV === 'development') {
        errorToast('Failed to fetch full thread');
      }
    },
  });

  return { updateMessage, getFullThread };
}
